import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { DateTime } from 'luxon';

import { isCapacitorNative } from 'environments/environment-helpers';
import { markCallerReportsFailure } from 'app/fetch-failure-classifier';
import { LoggingService } from 'services/logging.service';
import { PubSubService } from 'services/pubsub.service';
import { ConnectionState, connectionStateChangedEvent, OnlineStateService } from 'services/online-state.service';
import { blobToBase64Async } from 'services/image-upload-outbox.service';
import { describeError, describeErrorForLog } from '@cf-shared/error-describe';
import {
  CacheEpoch, CacheEpochToken, cacheFileNameFor, isCacheableImageUrl, isCacheFileName, isFileMissingError, isFolderAlreadyExistsError, isThumbnailSize,
  NativeImageCacheEntry, parseCacheIndex, selectEvictions, serializeCacheIndex,
} from 'services/native-image-cache-rules';
import type { UserDb } from 'services/user-db.service';

// #5692 — the offline image cache for the iOS and Android apps. The web app keeps images offline
// through its service worker (client/templates/sw.js), which the native apps never register. This
// service is their equivalent: it saves image bytes as files on the device and hands the <img> a
// local file address, so a photo seen once (and every thumbnail of the user's own foods) still
// shows with no network. Full design: design-docs/image-uploads.md, "Native image cache (#5692)".
//
// - ImageService.getDisplayUrl asks `localUrlFor` synchronously, so every screen that shows a food
//   photo goes through this one lookup. Until the index has loaded, the answer is "not cached" and
//   the remote URL is shown, exactly as before.
// - `request` queues a background download; at most MAX_PARALLEL_DOWNLOADS run at once, each URL
//   is tried once per app session, and nothing downloads while the device is offline.
// - The files live in CACHE_FOLDER under Directory.LibraryNoCloud: not backed up to iCloud, and
//   not cleared by iOS under storage pressure the way Caches/ is. index.json in the same folder
//   says which URL each file holds.
// - Sign-out (`stopAndDeleteAsync`) and "Delete Private Data" (`clearAsync`) delete the whole
//   folder. Both first move `epoch` on and wait for the file writes already under way. Every
//   write goes through `writeIfCurrentAsync`, which refuses one whose work began before that, so
//   a download in flight cannot put the previous user's photo back afterwards.
// - On the web, and on an install built before this plugin existed, the service is off and every
//   method answers "not cached".

const CACHE_FOLDER = 'image-cache';
const INDEX_NAME = 'index.json';
const INDEX_FILE = `${CACHE_FOLDER}/${INDEX_NAME}`;
const CACHE_DIRECTORY = Directory.LibraryNoCloud;
const MAX_PARALLEL_DOWNLOADS = 2;
// the index is written back at most this often, not on every change
const INDEX_WRITE_DELAY_MS = 5000;
// a file's "last shown" time is only moved forward when it is at least this old, so a screen that
// redraws many times a second does not mark the index as changed every time
const LAST_USED_RESOLUTION_MS = 60_000;

/**
 * The platform pieces the cache uses. In the app they are the real Filesystem plugin and
 * Capacitor; a unit test passes fakes, because none of them work under Node.
 */
export interface NativeImageCachePlatform {
  /** whether this platform can run the cache: a native app whose binary includes the plugin */
  available: boolean;
  filesystem: Pick<typeof Filesystem, 'getUri' | 'stat' | 'mkdir' | 'readFile' | 'writeFile' | 'readdir' | 'rmdir' | 'deleteFile'>;
  convertFileSrc: (fileUri: string) => string;
  blobToBase64Async: (blob: Blob) => Promise<string>;
}

/** The real platform pieces, and the one place that decides whether the cache can run. A unit
 * test passes its own object to the service's constructor instead. */
export const NATIVE_IMAGE_CACHE_PLATFORM = new InjectionToken<NativeImageCachePlatform>('NativeImageCachePlatform', {
  providedIn: 'root',
  factory: () => ({
    available: isCapacitorNative() && Capacitor.isPluginAvailable('Filesystem'),
    filesystem: Filesystem,
    convertFileSrc: (fileUri: string) => Capacitor.convertFileSrc(fileUri),
    blobToBase64Async,
  }),
});

/** The hex SHA-256 of a URL, which names its cache file. */
async function sha256HexAsync(text: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('');
}

@Injectable({ providedIn: 'root' })
export class NativeImageCacheService {
  readonly isEnabled: boolean;
  private readonly fs: NativeImageCachePlatform['filesystem'];

  // url -> cached file. only filled once the index has loaded
  private entries = new Map<string, NativeImageCacheEntry>();
  // file:// address of the cache folder, resolved once at start
  private folderUri: string | null = null;
  private started: Promise<void> | null = null;
  private prefetchStarted = false;
  // set by sign-out: nothing more is downloaded or written until the app reloads
  private stopped = false;
  // moved on by every clear. work that began under an older epoch does not store its result
  private epoch = new CacheEpoch();
  // the file writes under way, so a clear can wait for them before it deletes the folder
  private writesInFlight = new Set<Promise<unknown>>();
  // true while a clear runs: no new download starts until the folder is gone
  private clearing = false;
  // the one creation of the cache folder that every save waits for, so two saves never create it
  // at the same moment (#6392). null until the first save, and again after a clear deleted it
  private folderReady: Promise<void> | null = null;

  // download queue. `attempted` holds every URL tried this session, so a URL that fails is not
  // retried on every redraw
  private waiting: string[] = [];
  private attempted = new Set<string>();
  private running = 0;

  private indexWriteTimer: ReturnType<typeof setTimeout> | null = null;
  // problem names already reported this session: one report per kind is enough to see it
  private reported = new Set<string>();

  constructor(
    private logging: LoggingService,
    private pubsub: PubSubService,
    private onlineState: OnlineStateService,
    @Inject(NATIVE_IMAGE_CACHE_PLATFORM) private platform: NativeImageCachePlatform,
  ) {
    this.isEnabled = platform.available;
    this.fs = platform.filesystem;
    if (!this.isEnabled) {
      return;
    }
    // resume waiting downloads when the connection returns. a root singleton, so no teardown
    this.pubsub.subscribe(connectionStateChangedEvent, (state: ConnectionState) => {
      if (state === ConnectionState.Online) {
        this.pump();
      }
    });
  }

  /**
   * Load the index, then fetch the thumbnails of the user's own foods once the first sync has
   * settled. Safe to call from both the cold and the warm boot path: the load happens once, and so
   * does the background fetch.
   */
  startAsync(userDb: UserDb): Promise<void> {
    if (!this.isEnabled) {
      return Promise.resolve();
    }
    if (!this.started) {
      // images asked for while the index loaded were queued but could not start; start them now
      this.started = this.loadIndexAsync().then(() => this.pump());
    }
    return this.started.then(() => {
      if (this.prefetchStarted) {
        return;
      }
      this.prefetchStarted = true;
      const unsubscribe = userDb.database.onInitialSyncSettled(() => {
        unsubscribe();
        void this.prefetchOwnFoodThumbnailsAsync(userDb);
      });
    });
  }

  /**
   * The local file address for this image URL, or undefined when it is not cached. Synchronous,
   * because Angular templates call it while drawing. A hit counts as the image being shown.
   */
  localUrlFor(url: string | undefined | null): string | undefined {
    if (!this.folderUri || !isCacheableImageUrl(url)) {
      return undefined;
    }
    const entry = this.entries.get(url);
    if (!entry) {
      return undefined;
    }
    const now = DateTime.now().toMillis();
    if (now - entry.lastUsedMs > LAST_USED_RESOLUTION_MS) {
      entry.lastUsedMs = now;
      this.scheduleIndexWrite();
    }
    return this.platform.convertFileSrc(`${this.folderUri}/${entry.file}`);
  }

  /** Queue this image for download when it is cacheable and not already cached or tried. */
  request(url: string | undefined | null): void {
    if (!this.isEnabled || this.stopped || !isCacheableImageUrl(url) || this.entries.has(url) || this.attempted.has(url)) {
      return;
    }
    this.attempted.add(url);
    this.waiting.push(url);
    this.pump();
  }

  /**
   * An <img> could not load `failedSrc`. When that was one of our files (removed by the system,
   * or damaged), forget it and answer the remote URL the image should fall back to. Otherwise
   * answer undefined: the failure was not ours.
   */
  forgetLocalCopy(failedSrc: string | undefined | null): string | undefined {
    if (!this.folderUri || !failedSrc) {
      return undefined;
    }
    for (const [url, entry] of this.entries) {
      if (failedSrc === this.platform.convertFileSrc(`${this.folderUri}/${entry.file}`)) {
        this.entries.delete(url);
        this.scheduleIndexWrite();
        void this.deleteFileAsync(entry.file);
        return url;
      }
    }
    return undefined;
  }

  /**
   * Forget every cached image and delete the folder. For "Delete Private Data", where the user
   * stays signed in, so the in-memory index must go as well as the files, and caching carries on
   * afterwards. Throws when the folder delete fails, so the caller reports it.
   */
  async clearAsync(): Promise<void> {
    if (!this.isEnabled) {
      return;
    }
    await this.forgetEverythingAsync();
  }

  /**
   * Stop caching for good and delete the folder. For the sign-out wipe: the photos belong to the
   * user who is leaving, and the app reloads right after. Throws when the folder delete fails, so
   * the caller reports it.
   */
  async stopAndDeleteAsync(): Promise<void> {
    if (!this.isEnabled) {
      return;
    }
    this.stopped = true;
    await this.forgetEverythingAsync();
  }

  // empty the index and the queue, make running downloads drop their result, wait for the file
  // writes already under way, then delete the folder
  private async forgetEverythingAsync(): Promise<void> {
    this.epoch.advance();
    this.clearing = true;
    if (this.indexWriteTimer) {
      clearTimeout(this.indexWriteTimer);
      this.indexWriteTimer = null;
    }
    this.entries.clear();
    this.waiting = [];
    this.attempted.clear();
    try {
      await Promise.allSettled([...this.writesInFlight]);
      await this.deleteCacheFolderAsync();
      // the next save creates the folder again
      this.folderReady = null;
    } finally {
      this.clearing = false;
    }
    // images asked for while the clear ran were queued; start them now
    this.pump();
  }

  // delete the whole cache folder. a folder that is already gone is not a failure; any other
  // failure is thrown for the caller to report
  private async deleteCacheFolderAsync(): Promise<void> {
    try {
      await this.fs.rmdir({ path: CACHE_FOLDER, directory: CACHE_DIRECTORY, recursive: true });
    } catch (err) {
      if (!isFileMissingError(err)) {
        throw err;
      }
    }
  }

  // whether the cache folder exists. throws on any failure other than "not there"
  private async cacheFolderExistsAsync(): Promise<boolean> {
    try {
      await this.fs.stat({ path: CACHE_FOLDER, directory: CACHE_DIRECTORY });
      return true;
    } catch (err) {
      if (isFileMissingError(err)) {
        return false;
      }
      throw err;
    }
  }

  // resolve the folder address and read the index, repairing it when damaged. on any failure the
  // cache stays off for this session and photos load remotely as they did before #5692
  private async loadIndexAsync(): Promise<void> {
    const token = this.epoch.begin();
    let folderUri: string;
    try {
      const folder = await this.fs.getUri({ path: CACHE_FOLDER, directory: CACHE_DIRECTORY });
      folderUri = folder.uri.replace(/\/$/, '');
    } catch (err) {
      this.reportProblemOnce('NativeImageCacheStartFailed', { message: describeError(err) });
      return;
    }
    try {
      if (await this.cacheFolderExistsAsync()) {
        const entries = await this.readAndRepairIndexAsync();
        // a clear that ran meanwhile deleted the files these rows name
        if (this.epoch.isCurrent(token)) {
          this.entries = entries;
        }
      }
    } catch (err) {
      // if a clear deleted the folder while it was being read, that explains the failure: the
      // index is gone on purpose and caching carries on empty. anything else keeps the cache off
      if (this.epoch.isCurrent(token)) {
        this.reportProblemOnce('NativeImageCacheStartFailed', { message: describeError(err) });
        return;
      }
    }
    this.folderUri = folderUri;
  }

  // the index's usable rows. a damaged index keeps what it can, and the files no kept row names
  // are deleted so nothing sits on disk outside the limits
  private async readAndRepairIndexAsync(): Promise<Map<string, NativeImageCacheEntry>> {
    const { raw, unreadable } = await this.readIndexJsonAsync();
    const { entries, damaged } = parseCacheIndex(raw);
    if (!damaged) {
      return entries;
    }
    await this.deleteUnindexedFilesAsync(entries);
    this.scheduleIndexWrite();
    // info, not a problem: the repair has run by the time this is reported
    this.logging.trackSilentInfo('NativeImageCacheIndexRepaired', { kept: entries.size, unreadable });
    return entries;
  }

  // the index file's parsed JSON. a missing file answers raw null; text that is cut short or not
  // JSON answers raw null plus why, which the repair report carries. any other read failure throws
  private async readIndexJsonAsync(): Promise<{ raw: unknown; unreadable?: string }> {
    let text: string;
    try {
      const file = await this.fs.readFile({ path: INDEX_FILE, directory: CACHE_DIRECTORY, encoding: Encoding.UTF8 });
      text = typeof file.data === 'string' ? file.data : await file.data.text();
    } catch (err) {
      if (isFileMissingError(err)) {
        return { raw: null, unreadable: 'index file missing' };
      }
      throw err;
    }
    try {
      return { raw: JSON.parse(text) };
    } catch (err) {
      return { raw: null, unreadable: describeError(err) };
    }
  }

  // delete every cache file in the folder that no kept row names
  private async deleteUnindexedFilesAsync(kept: Map<string, NativeImageCacheEntry>): Promise<void> {
    const keptFiles = new Set([...kept.values()].map(entry => entry.file));
    const listing = await this.fs.readdir({ path: CACHE_FOLDER, directory: CACHE_DIRECTORY });
    // for each file in the folder: delete it if it looks like ours and nothing points at it
    for (const file of listing.files) {
      if (file.name !== INDEX_NAME && isCacheFileName(file.name) && !keptFiles.has(file.name)) {
        await this.deleteFileAsync(file.name);
      }
    }
  }

  // queue the thumbnail of every food in the user's database that has a photo
  private async prefetchOwnFoodThumbnailsAsync(userDb: UserDb): Promise<void> {
    try {
      const foods = await userDb.getAllFoodsAsync();
      // for each food with a photo: queue its thumbnail, the size the food cards show
      for (const food of foods) {
        this.request(food.image?.sizes?.find(isThumbnailSize)?.url);
      }
    } catch (err) {
      // not fatal: thumbnails still get cached as they are shown
      this.reportProblemOnce('NativeImageCachePrefetchFailed', { message: describeError(err) });
    }
  }

  // start queued downloads up to the parallel limit, while the device looks online
  private pump(): void {
    while (!this.stopped && !this.clearing && this.folderUri && this.running < MAX_PARALLEL_DOWNLOADS && this.waiting.length > 0
      && this.onlineState.isLikelyOnline()) {
      const url = this.waiting.shift() as string;
      // cached since it was queued (the index loaded after the request): nothing to fetch
      if (this.entries.has(url)) {
        continue;
      }
      this.running++;
      void this.downloadAsync(url).finally(() => {
        this.running--;
        this.pump();
      });
    }
  }

  // fetch one image and store it. a result that arrives after a clear is dropped, and a download
  // cut short by going offline waits in the queue for the connection to return
  private async downloadAsync(url: string): Promise<void> {
    const token = this.epoch.begin();
    const bytes = await this.fetchBytesAsync(url);
    // after a clear the save below would be refused anyway; this only skips the encode
    if (!bytes || !this.epoch.isCurrent(token)) {
      return;
    }
    let file: string;
    try {
      file = cacheFileNameFor(await sha256HexAsync(url), url);
      const data = await this.platform.blobToBase64Async(bytes);
      // the same, for a clear that started during the encode: skip creating the folder for nothing
      if (!this.epoch.isCurrent(token)) {
        return;
      }
      await this.ensureFolderAsync(token);
      await this.writeIfCurrentAsync(token,
        () => this.fs.writeFile({ path: `${CACHE_FOLDER}/${file}`, data, directory: CACHE_DIRECTORY, recursive: true }));
    } catch (err) {
      // a full disk or a broken plugin: worth seeing, once
      this.reportProblemOnce('NativeImageCacheWriteFailed', { message: describeError(err), bytes: bytes.size });
      return;
    }
    // a clear that started before the write refused it, and one that started during the write
    // waited for it and then deleted the folder with it. either way there is no file to index
    if (!this.epoch.isCurrent(token)) {
      return;
    }
    this.entries.set(url, { file, bytes: bytes.size, lastUsedMs: DateTime.now().toMillis() });
    this.evictOverLimit();
    this.scheduleIndexWrite();
  }

  // the image's bytes, or undefined when it cannot be fetched now
  private async fetchBytesAsync(url: string): Promise<Blob | undefined> {
    try {
      // marked so the global fetch wrapper only logs a failure: this method decides what one is
      // worth (see the catch below), and a report from the wrapper was Sentry noise (3XY)
      const response = await fetch(url, markCallerReportsFailure({}));
      if (!response.ok) {
        // a missing or refused object: expected for deleted photos, nothing to keep
        console.log(`NativeImageCache: ${url} answered HTTP ${response.status}, not cached`);
        return undefined;
      }
      return await response.blob();
    } catch (err) {
      // a flaky network is expected, and the photo still shows from its remote address. cut short
      // by going offline, it waits for the connection to return; otherwise it is tried again next
      // session
      console.log(`NativeImageCache: download of ${url} failed, not cached`, describeErrorForLog(err));
      if (!this.onlineState.isLikelyOnline()) {
        this.attempted.delete(url);
        this.request(url);
      }
      return undefined;
    }
  }

  // make sure the cache folder exists before a save writes into it. the folder is created once and
  // every save waits for that, because on Android two saves that each create the missing folder
  // at the same moment can make one of them fail with "Missing parent directory" (#6392). the
  // saves keep `recursive: true` in case the system removes the folder later in the session. a
  // failed creation is thrown to the save, which reports it, and is tried again by the next save.
  // a save that began before a clear creates nothing and is not remembered as the creation: it
  // would put the deleted folder back, and later saves would wait on a creation that never ran.
  // its own write is refused next
  private ensureFolderAsync(token: CacheEpochToken): Promise<void> {
    if (!this.folderReady) {
      if (!this.epoch.isCurrent(token)) {
        return Promise.resolve();
      }
      const ready = this.writeIfCurrentAsync(token, () => this.createFolderAsync());
      ready.catch(() => {
        if (this.folderReady === ready) {
          this.folderReady = null;
        }
      });
      this.folderReady = ready;
    }
    return this.folderReady;
  }

  // create the cache folder. one that is already there counts as created
  private async createFolderAsync(): Promise<void> {
    try {
      await this.fs.mkdir({ path: CACHE_FOLDER, directory: CACHE_DIRECTORY, recursive: true });
    } catch (err) {
      if (!isFolderAlreadyExistsError(err)) {
        throw err;
      }
    }
  }

  // the one way this service writes or creates anything on disk. if a clear has happened since
  // the work began, the write is skipped: that clear has deleted, or is about to delete, the
  // folder the result belongs in. otherwise the write starts and is remembered until it settles,
  // so a clear that starts meanwhile waits for it. the check and the start happen with no await
  // between them, and a clear moves the epoch on and takes its list of writes the same way, so
  // every write is either waited for by the clear or skipped. callers that do more after the write
  // ask the epoch again
  private async writeIfCurrentAsync(token: CacheEpochToken, write: () => Promise<unknown>): Promise<void> {
    if (!this.epoch.isCurrent(token)) {
      return;
    }
    const running = write();
    this.writesInFlight.add(running);
    try {
      await running;
    } finally {
      this.writesInFlight.delete(running);
    }
  }

  // remove the least recently shown files until the cache fits its limits
  private evictOverLimit(): void {
    for (const url of selectEvictions(this.entries)) {
      const entry = this.entries.get(url);
      this.entries.delete(url);
      if (entry) {
        void this.deleteFileAsync(entry.file);
      }
    }
  }

  // delete one cache file. its row is already gone, so a failure leaves a file nothing counts:
  // reported, since it means the plugin is not doing what it should
  private async deleteFileAsync(file: string): Promise<void> {
    try {
      await this.fs.deleteFile({ path: `${CACHE_FOLDER}/${file}`, directory: CACHE_DIRECTORY });
    } catch (err) {
      if (!isFileMissingError(err)) {
        this.reportProblemOnce('NativeImageCacheDeleteFailed', { message: describeError(err) });
      }
    }
  }

  // write the index back after INDEX_WRITE_DELAY_MS. while one write is already scheduled this
  // does nothing: that write will carry this change too
  private scheduleIndexWrite(): void {
    if (this.indexWriteTimer || this.stopped) {
      return;
    }
    this.indexWriteTimer = setTimeout(() => {
      this.indexWriteTimer = null;
      void this.writeIndexAsync();
    }, INDEX_WRITE_DELAY_MS);
  }

  // write the in-memory index to index.json
  private async writeIndexAsync(): Promise<void> {
    if (this.stopped) {
      return;
    }
    const token = this.epoch.begin();
    try {
      await this.ensureFolderAsync(token);
      // signed out while the folder was being created: nothing more is written
      if (this.stopped) {
        return;
      }
      await this.writeIfCurrentAsync(token, () => this.fs.writeFile({
        path: INDEX_FILE,
        data: serializeCacheIndex(this.entries),
        directory: CACHE_DIRECTORY,
        encoding: Encoding.UTF8,
        recursive: true,
      }));
    } catch (err) {
      this.reportProblemOnce('NativeImageCacheWriteFailed', { message: describeError(err), file: 'index' });
    }
  }

  // report a problem to Sentry, at most once per kind per session
  private reportProblemOnce(problemName: string, context: Record<string, unknown>): void {
    if (this.reported.has(problemName)) {
      return;
    }
    this.reported.add(problemName);
    this.logging.trackSilentProblem(problemName, context);
  }
}
